import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { CaptchaHandle, CaptchaResult } from './captcha-types';
import { loadTencentCaptcha } from './tencent-loader';

type Props = {
  onSuccess: (result: CaptchaResult) => void;
  onError: (kind: 'network' | 'javascript' | 'closed', error?: unknown) => void;
};

/**
 * Invisible/senseless Tencent captcha (mode configured in the Tencent console).
 * `execute()` shows the challenge only when the risk engine flags; otherwise the
 * ticket returns with no UI — same UX as Yandex InvisibleSmartCaptcha.
 */
export const TencentCaptcha = forwardRef<CaptchaHandle, Props>(
  ({ onSuccess, onError }, ref) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const instanceRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Предзагрузка скрипта в фоне (компонент монтируется только когда конфиг
    // уже выбрал tencent) — клик по execute() не платит за скачивание. Ошибку
    // глотаем: юзер ещё ничего не запрашивал, а лоадер сбрасывает кэш при
    // onerror, так что клик-путь сам повторит загрузку и отрепортит onError.
    useEffect(() => {
      loadTencentCaptcha().catch(() => {});
    }, []);

    useImperativeHandle(ref, () => ({
      execute: () => {
        loadTencentCaptcha()
          .then((TencentCaptchaCtor) => {
            // Guard against orphaning a prior instance if execute() runs twice
            // without an intervening reset() (destroy() is a no-op if absent).
            instanceRef.current?.destroy?.();
            // Element-first ctor: pass a container. In senseless mode it stays
            // empty; a flagged challenge renders as a centered floating popup
            // regardless of the container.
            instanceRef.current = new TencentCaptchaCtor(
              containerRef.current!,
              import.meta.env.VITE_TENCENT_CAPTCHA_APP_ID || '',
              (res) => {
                if (res.ret === 0 && res.ticket && res.randstr) {
                  onSuccess({
                    provider: 'tencent',
                    ticket: res.ticket,
                    randstr: res.randstr,
                  });
                } else if (res.ret === 2) {
                  // user closed the challenge — normal, not an error. No Sentry.
                  onError('closed');
                } else {
                  onError('javascript', res);
                }
              },
            );
            instanceRef.current.show();
          })
          .catch((error) => onError('network', error));
      },
      reset: () => {
        instanceRef.current?.destroy?.();
        instanceRef.current = null;
      },
    }));

    // Container required by the element-first constructor. Empty in senseless
    // mode; a flagged challenge shows as a centered floating popup.
    return <div ref={containerRef} />;
  },
);
TencentCaptcha.displayName = 'TencentCaptcha';
